import { useEffect, useMemo, useRef, useState } from "react";
import api, { fetchAuthOpts, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, Eye, Mail, FileText, X, Upload, Image as ImageIcon, Trash2, MessageSquare, Sparkles, RefreshCw } from "lucide-react";
import { LangStatusIcon, shortLang, statusTitle } from "@/components/design/langStatus";
import { TemplateTranslationView } from "@/components/design/TemplateTranslationView";
import { PublicTextsEditor } from "@/components/design/PublicTextsEditor";
import { Globe } from "lucide-react";

const PUBLIC = [{ key: "quote_page", label: "Presupuesto público" }];

const LS_KEY = "design.i18n.state";
const readLS = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch { return {}; } };

const EMAILS = [
  { key: "invoice_send", label: "Email envío factura", icon: FileText },
  { key: "credit_note_send", label: "Email envío abono", icon: FileText },
  { key: "delivery_note_send", label: "Email envío albarán", icon: FileText },
  { key: "quote_send", label: "Email envío presupuesto", icon: FileText },
  { key: "quote_send_whatsapp", label: "Envío presupuesto · WhatsApp", icon: MessageSquare },
  { key: "quote_accepted", label: "Email presupuesto aceptado (cliente)", icon: FileText },
  { key: "quote_rejected", label: "Email presupuesto rechazado (cliente)", icon: FileText },
  { key: "checklist_offer", label: "Oferta por incidencia de Checklist · Email", icon: FileText },
  { key: "checklist_offer_whatsapp", label: "Oferta por incidencia de Checklist · WhatsApp", icon: MessageSquare },
  { key: "repair_status", label: "Email aviso reparación", icon: FileText },
  { key: "estimated_date_set", label: "Fecha estimada asignada · Email", icon: Mail },
  { key: "b2b_received", label: "Solicitud B2B recibida · Email", icon: Mail },
  { key: "pickup_request", label: "Solicitud de recogida · Mensajería · Email", icon: Mail },
  { key: "pickup_confirmation", label: "Confirmación de recogida al cliente · Email", icon: Mail },
  { key: "generic_customer", label: "Email general cliente", icon: FileText },
  { key: "repair_signed", label: "Email de reparación firmada", icon: FileText },
  { key: "customer_signature_request", label: "Solicitud de firma del cliente", icon: FileText },
  { key: "notify_with_repair_email", label: "Aviso equipo listo · CON reparación · Email", icon: Mail },
  { key: "notify_without_repair_email", label: "Aviso equipo listo · SIN reparación · Email", icon: Mail },
  { key: "notify_received_no_quote_email", label: "Recepción del equipo sin presupuesto · Email", icon: Mail },
  { key: "notify_with_repair_whatsapp", label: "Aviso equipo listo · CON reparación · WhatsApp", icon: MessageSquare },
  { key: "notify_without_repair_whatsapp", label: "Aviso equipo listo · SIN reparación · WhatsApp", icon: MessageSquare },
  { key: "notify_received_no_quote_whatsapp", label: "Recepción del equipo sin presupuesto · WhatsApp", icon: MessageSquare },
];
const DOCS = [
  { key: "invoice", label: "Factura" },
  { key: "credit_note", label: "Abono" },
  { key: "delivery_note", label: "Albarán" },
  { key: "quote", label: "Presupuesto" },
  { key: "repair_label", label: "Etiqueta de reparación" },
];

const VAR_GROUPS = [
  {
    label: "Empresa",
    vars: ["empresa_nombre","empresa_nif","empresa_direccion","empresa_cp","empresa_poblacion","empresa_provincia","empresa_pais","empresa_telefono","empresa_email","empresa_web","empresa_logo"],
  },
  {
    label: "Cliente",
    vars: ["cliente_numero","cliente_nombre","cliente_nif","cliente_direccion","cliente_cp","cliente_poblacion","cliente_provincia","cliente_pais","cliente_telefono","cliente_email"],
  },
  {
    label: "Documento",
    vars: ["documento_tipo","documento_numero","documento_fecha","documento_hora","documento_observaciones"],
  },
  {
    label: "Económicas",
    vars: ["base_imponible","iva_porcentaje","importe_iva","total_documento","forma_pago"],
  },
  {
    label: "Reparación",
    vars: ["reparacion_numero","equipo","numero_serie_imei","diagnostico_sintomas","tecnico_designado","reparacion_id","numero_serie","estado_reparacion","fecha_entrada","fecha_estimada","observaciones","concepto_reparacion","importe_total","link_portal_cliente","enlace_presupuesto","fecha_decision","presupuesto_original","total_addons","total_aceptado","lineas_presupuesto_html","addons_html","aviso_interno_html"],
  },
];

// Variables disponibles en el editor HTML5 de plantillas de email. Se muestran
// como chips clicables debajo del editor.
const EMAIL_VARS = [
  "cliente_nombre", "cliente_email", "cliente_telefono",
  "reparacion_id", "equipo", "modelo", "numero_serie",
  "fecha_entrada", "fecha_estimada", "estado_reparacion",
  "incidencia_checklist", "productos_ofertados", "productos_ofertados_html", "importe_oferta", "descuento_oferta", "link_presupuesto",
  "numero_factura", "numero_albaran", "numero_presupuesto", "numero_abono",
  "importe_total",
  "empresa_nombre", "empresa_telefono", "empresa_email", "empresa_direccion",
  "link_portal_cliente", "firma_cliente",
];

const COLUMN_LABELS = {
  repair_id: "ID Reparación",
  sku: "Código (SKU)",
  name: "Concepto / nombre",
  quantity: "Cantidad",
  unit_price: "Precio unitario",
  vat_rate: "IVA %",
  line_total: "Total línea",
};

export default function Design({ embedded = false }) {
  const saved = useMemo(readLS, []);
  const [tab, setTab] = useState(saved.tab || "documents"); // emails | documents
  const [docKey, setDocKey] = useState(saved.docKey || "invoice");
  const [emailKey, setEmailKey] = useState(saved.emailKey || "invoice_send");
  const [lang, setLang] = useState(saved.lang || "");
  const [langs, setLangs] = useState([]);
  const [master, setMaster] = useState("es-ES");
  const [i18n, setI18n] = useState({});
  const [bulk, setBulk] = useState(null); // { done, total }
  const [data, setData] = useState(null);
  const [companyLogo, setCompanyLogo] = useState("");  // data URI

  const loadI18n = async () => {
    try {
      const r = await api.get("/templates/i18n/status");
      setI18n(r.data.status || {});
      setMaster(r.data.master);
      setLangs([{ code: r.data.master, name: "Español", is_master: true }, ...(r.data.languages || [])]);
      return r.data;
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = async () => {
    try {
      const [r, s] = await Promise.all([
        api.get("/templates"),
        api.get("/settings"),
      ]);
      setData(r.data);
      setCompanyLogo(s.data?.company?.logo_data_b64 || "");
      await loadI18n();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { localStorage.setItem(LS_KEY, JSON.stringify({ tab, docKey, emailKey, lang })); }, [tab, docKey, emailKey, lang]);

  const currentKey = tab === "documents" ? docKey : tab === "public" ? "quote_page" : emailKey;
  const currentGroup = tab === "documents" ? "documents" : tab === "public" ? "public" : "emails";
  const currentTpl = data ? (data[currentGroup] || {})[currentKey] : null;
  const LIST = tab === "documents" ? DOCS : tab === "public" ? PUBLIC : EMAILS;
  const activeLang = langs.some((l) => l.code === lang) ? lang : master;
  const isMaster = activeLang === master;
  const langName = langs.find((l) => l.code === activeLang)?.name || activeLang;
  const tplStatus = i18n[`${currentGroup}.${currentKey}`] || {};
  const currentLabel = LIST.find((t) => t.key === currentKey)?.label || currentKey;
  const publicSections = data?.public_sections || [];
  const fieldLabels = tab === "public" ? Object.fromEntries(publicSections.map((sec) => [sec.key, sec.label])) : undefined;

  const notifyI18n = (res) => {
    if (!res || !res.changed_fields?.length) return;
    const parts = [];
    if (res.regenerated?.length) parts.push(`traducciones actualizadas: ${res.regenerated.map(shortLang).join(", ")}`);
    if (res.outdated?.length) parts.push(`pendientes de revisar: ${res.outdated.map(shortLang).join(", ")}`);
    if (res.errors?.length) toast.error(`Errores de traducción: ${res.errors.map((e) => `${shortLang(e.language)} · ${e.error}`).join(" / ")}`);
    if (parts.length) toast.info(parts.join(" · "));
  };

  const save = async (patch) => {
    try {
      const r = await api.put(`/templates/${currentGroup}/${currentKey}`, patch);
      // refresh whole list
      const fresh = await api.get("/templates");
      setData(fresh.data);
      toast.success("Plantilla guardada");
      notifyI18n(r.data?.i18n);
      loadI18n();
      return r.data;
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reset = async () => {
    if (!window.confirm("¿Seguro que quieres restaurar esta plantilla? Se perderán los cambios actuales.")) return;
    try {
      const r = await api.post(`/templates/${currentGroup}/${currentKey}/reset`);
      const fresh = await api.get("/templates");
      setData(fresh.data);
      toast.success("Plantilla restaurada");
      notifyI18n(r.data?.i18n);
      loadI18n();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const translatePending = async () => {
    const targets = [];
    for (const [g, list] of [["documents", DOCS], ["emails", EMAILS], ["public", PUBLIC]]) {
      for (const t of list) {
        const st = i18n[`${g}.${t.key}`]?.[activeLang];
        if (st && st.status !== "updated" && !st.reviewed) targets.push([g, t.key]);
      }
    }
    if (!targets.length) { toast.info(`No hay plantillas pendientes en ${langName}`); return; }
    if (!window.confirm(`Se traducirán ${targets.length} plantilla(s) pendientes a ${langName} con OpenAI. ¿Continuar?`)) return;
    setBulk({ done: 0, total: targets.length });
    const errors = [];
    for (let i = 0; i < targets.length; i++) {
      const [g, k] = targets[i];
      try { await api.post(`/templates/i18n/${g}/${k}/${activeLang}/translate`, { force: false }); }
      catch (e) { errors.push(`${k}: ${formatApiError(e)}`); }
      setBulk({ done: i + 1, total: targets.length });
    }
    setBulk(null);
    await loadI18n();
    if (errors.length) toast.error(`Errores: ${errors.join(" / ")}`);
    else toast.success(`${targets.length} plantilla(s) traducidas a ${langName}`);
  };

  const previewPdf = async () => {
    if (currentGroup !== "documents") return;
    try {
      const r = await fetch(`${api.defaults.baseURL}/templates/documents/${docKey}/preview?format=pdf`, fetchAuthOpts());
      if (!r.ok) { toast.error("Error generando vista previa"); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch { toast.error("Error generando vista previa"); }
  };

  if (!data) {
    return <div className="p-10 text-sm text-[#525252]">Cargando plantillas...</div>;
  }

  return (
    <div className={embedded ? "" : "min-h-screen flex flex-col"}>
      {!embedded && (
        <header className="px-6 lg:px-10 pt-6 lg:pt-8 pb-4 border-b border-[#E5E5E0] bg-[#F7F7F4]">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#6E8B3D]">
                // Plantillas de emails y documentos
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-black mt-2">Diseño</h1>
              <p className="text-sm text-[#525252] mt-1">
                Edita los textos, encabezados, pies y columnas que aparecen en los emails y PDFs enviados al cliente.
                {data.meta?.updated_at && (
                  <span className="ml-2 font-mono text-[10px] text-[#525252]">
                    · última edición {new Date(data.meta.updated_at).toLocaleString("es")} por {data.meta.updated_by_email}
                  </span>
                )}
              </p>
            </div>
          </div>
        </header>
      )}

      {/* Logo + tabs */}
      <div className={`${embedded ? "" : "px-6 lg:px-10 pt-4 pb-2 bg-[#F7F7F4] border-b border-[#E5E5E0]"} space-y-4`}>
        <LogoUploader value={companyLogo} onChange={setCompanyLogo} />
        <div className="flex items-center flex-wrap gap-3">
        <div className="inline-flex h-9 border border-[#111110]">
          <button
            onClick={() => setTab("documents")}
            data-testid="design-tab-documents"
            className={`px-3 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 ${
              tab === "documents" ? "bg-[#111110] text-white" : "bg-white"
            }`}
          >
            <FileText size={12} /> Documentos
          </button>
          <button
            onClick={() => setTab("emails")}
            data-testid="design-tab-emails"
            className={`px-3 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 ${
              tab === "emails" ? "bg-[#111110] text-white" : "bg-white"
            }`}
          >
            <Mail size={12} /> Emails
          </button>
          <button
            onClick={() => setTab("public")}
            data-testid="design-tab-public"
            className={`px-3 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 ${
              tab === "public" ? "bg-[#111110] text-white" : "bg-white"
            }`}
          >
            <Globe size={12} /> Página pública
          </button>
        </div>
        {langs.length > 1 && (
          <div className="inline-flex h-9 border border-[#111110]" data-testid="design-lang-tabs">
            {langs.map((l) => {
              const st = l.is_master ? null : tplStatus[l.code];
              const active = activeLang === l.code;
              return (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  data-testid={`design-lang-${l.code}`}
                  data-status={st?.status || "master"}
                  title={l.is_master ? `${l.name} · idioma maestro` : `${l.name} · ${statusTitle(st)}`}
                  className={`px-3 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 border-l border-[#111110] first:border-l-0 ${
                    active ? "bg-[#111110] text-white" : "bg-white hover:bg-[#F7F7F4]"
                  }`}
                >
                  {shortLang(l.code)}
                  {st && <LangStatusIcon st={st} />}
                </button>
              );
            })}
          </div>
        )}
        {!isMaster && (
          <button
            onClick={translatePending}
            disabled={!!bulk}
            data-testid="design-translate-pending"
            className="h-9 border border-[#111110] bg-white hover:bg-[#111110] hover:text-white disabled:opacity-60 px-3 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5"
          >
            {bulk ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {bulk ? `Traduciendo ${bulk.done}/${bulk.total}…` : `Traducir pendientes · ${shortLang(activeLang)}`}
          </button>
        )}
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[220px_1fr_260px]">
        {/* Left: list of templates */}
        <aside className="border-r border-[#E5E5E0] bg-white p-3 space-y-1">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] px-2 pb-1">
            {tab === "documents" ? "Documentos" : tab === "public" ? "Página pública" : "Emails"}
          </div>
          {LIST.map((t) => {
            const isActive = currentKey === t.key;
            return (
              <button
                key={t.key}
                onClick={() => tab === "documents" ? setDocKey(t.key) : setEmailKey(t.key)}
                data-testid={`design-pick-${t.key}`}
                className={`w-full text-left px-3 py-2 text-sm border ${
                  isActive ? "bg-[#111110] text-white border-[#111110]" : "border-transparent hover:bg-[#F7F7F4]"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </aside>

        {/* Center: editor */}
        <main className="p-6 lg:p-8 bg-[#F7F7F4] overflow-y-auto">
          {!isMaster ? (
            <TemplateTranslationView
              key={`${currentGroup}-${currentKey}-${activeLang}`}
              group={currentGroup}
              tplKey={currentKey}
              tplLabel={currentLabel}
              lang={activeLang}
              langName={langName}
              onChanged={loadI18n}
              fieldLabels={fieldLabels}
            />
          ) : tab === "public" ? (
            <PublicTextsEditor key={`public-${data?.meta?.updated_at || ""}`} tpl={currentTpl} sections={publicSections} onSave={save} onReset={reset} />
          ) : tab === "documents" ? (
            <DocEditor
              key={docKey}
              tplKey={docKey}
              tpl={currentTpl}
              onSave={save}
              onReset={reset}
              onPreview={previewPdf}
            />
          ) : (
            <EmailEditor
              key={emailKey}
              tplKey={emailKey}
              tpl={currentTpl}
              onSave={save}
              onReset={reset}
            />
          )}
        </main>

        {/* Right: variables panel */}
        <aside className="border-l border-[#E5E5E0] bg-white p-4 overflow-y-auto max-h-[calc(100vh-130px)]">
          <div className="font-display font-bold text-sm mb-2">Variables disponibles</div>
          <p className="text-[11px] text-[#525252] mb-3">
            Haz clic en una variable para copiarla. Pégala en cualquier campo de texto. La <strong>nota interna</strong> NUNCA se renderiza en documentos enviados al cliente.
          </p>
          {VAR_GROUPS.map((g) => (
            <div key={g.label} className="mb-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#6E8B3D] mb-1">{g.label}</div>
              <div className="flex flex-wrap gap-1">
                {g.vars.map((v) => (
                  <button
                    key={v}
                    onClick={() => {
                      navigator.clipboard.writeText(`{${v}}`);
                      toast.success(`{${v}} copiado`);
                    }}
                    data-testid={`design-var-${v}`}
                    className="font-mono text-[10px] px-1.5 py-0.5 border border-[#E5E5E0] hover:border-[#6E8B3D] hover:text-[#6E8B3D]"
                    title="Click para copiar"
                  >
                    {`{${v}}`}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </aside>
      </div>
    </div>
  );
}

function DocEditor({ tplKey, tpl, onSave, onReset, onPreview }) {
  const [form, setForm] = useState(tpl);
  useEffect(() => setForm(tpl), [tpl, tplKey]);
  const setF = (k, v) => setForm({ ...form, [k]: v });

  const toggleCol = (c) => {
    const cols = form.columns_visible || [];
    setF("columns_visible", cols.includes(c) ? cols.filter((x) => x !== c) : [...cols, c]);
  };

  return (
    <div className="ds-card p-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#6E8B3D]">
            // {DOCS.find((d) => d.key === tplKey)?.label}
          </div>
          <div className="font-display font-bold text-xl mt-1">Plantilla del PDF</div>
          <p className="text-xs text-[#525252] mt-1">
            Esta plantilla se aplica automáticamente al generar/enviar/imprimir un {DOCS.find((d) => d.key === tplKey)?.label.toLowerCase()}.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={onPreview} data-testid="design-preview" className="border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-2 text-xs font-mono uppercase inline-flex items-center gap-1">
            <Eye size={12} /> Vista previa PDF
          </button>
          <button onClick={onReset} data-testid="design-reset" className="border border-red-300 text-red-700 hover:bg-red-50 px-3 py-2 text-xs font-mono uppercase inline-flex items-center gap-1">
            <RotateCcw size={12} /> Restaurar
          </button>
        </div>
      </div>

      <Field label="Título del documento" value={form.title} onChange={(v) => setF("title", v)} testid="design-title" />
      {tplKey === "credit_note" && (
        <div className="border-2 border-[#6E8B3D] bg-[#F4F8EC] p-4" data-testid="credit-note-unified-notice">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#6E8B3D] font-bold mb-1.5">
            Plantilla unificada con Factura
          </div>
          <p className="text-sm text-[#111110]">
            El diseño del <b>Abono</b> es <b>idéntico</b> al de Factura: cabecera, logo, datos
            de empresa, bloque del cliente, barra de reparación, columnas, formato de precios,
            condiciones del servicio, protección de datos y pie de página se heredan
            automáticamente desde la plantilla de Factura.
          </p>
          <p className="text-xs text-[#525252] mt-2">
            Para personalizar el diseño general, edita la pestaña <b>Factura</b>. En este
            Abono sólo puedes ajustar los <b>3 campos específicos</b> abajo: título, texto
            legal del abono y pie del documento.
          </p>
        </div>
      )}
      {tplKey === "delivery_note" && (
        <Field
          label="Aviso legal del albarán"
          hint="Texto que aparece bajo el título ALBARÁN en el PDF. Si está vacío, no se muestra. Solo aplica a Albaranes."
          value={form.legal_notice ?? "NO VÁLIDO COMO FACTURA FISCAL"}
          onChange={(v) => setF("legal_notice", v)}
          testid="design-legal-notice"
        />
      )}
      {tplKey === "credit_note" ? (
        <>
          <Field
            label="Condiciones del servicio (texto legal específico del Abono)"
            hint="Aparece en el PDF bajo 'CONDICIONES DEL SERVICIO'. Si lo dejas vacío, hereda el valor de Factura."
            value={form.legal_terms ?? ""}
            onChange={(v) => setF("legal_terms", v)}
            multi
            testid="design-legal"
          />
          <Field
            label="Pie del documento (específico del Abono)"
            hint="Texto al final del PDF. Si lo dejas vacío, hereda el de Factura."
            value={form.footer_text ?? ""}
            onChange={(v) => setF("footer_text", v)}
            multi
            testid="design-footer"
          />
        </>
      ) : tplKey === "repair_label" ? (
        <LabelLinesEditor form={form} setF={setF} />
      ) : (
        <>
      <Field
        label={tplKey === "quote" ? "Notas por defecto del presupuesto" : "Observaciones por defecto"}
        hint={tplKey === "quote"
          ? "Aparecen en el PDF bajo 'NOTAS' si el presupuesto concreto no tiene una nota personalizada (escrita desde el botón 'Nota' de Reparación > Productos / Servicios)."
          : "Se muestran si el documento concreto no tiene observaciones propias"}
        value={form.default_observations} onChange={(v) => setF("default_observations", v)} multi testid="design-defobs"
      />
      <Field
        label={tplKey === "quote" ? "Condiciones del presupuesto" : "Condiciones del servicio"}
        hint={tplKey === "quote"
          ? "Aparece al final del PDF dentro del cuadro 'CONDICIONES DEL PRESUPUESTO'. Admite saltos de línea."
          : "Aparece en el PDF bajo el título 'CONDICIONES DEL SERVICIO'. Admite saltos de línea y texto largo."}
        value={form.legal_terms} onChange={(v) => setF("legal_terms", v)} multi testid="design-legal"
      />
      <Field
        label="Protección de datos (RGPD)"
        hint="Texto del bloque 'PROTECCIÓN DE DATOS' que aparece SIEMPRE al final del PDF (después de condiciones, totales y firmas). Aplica a presupuestos, albaranes, facturas y abonos. Admite saltos de línea y texto largo. Variables disponibles: {{empresa_nombre}}, {{empresa_email}}."
        value={form.data_protection ?? ""} onChange={(v) => setF("data_protection", v)} multi testid="design-data-protection"
      />
      {tplKey === "quote" && (
        <Field
          label="Validez del presupuesto (días)"
          hint="Se muestra en el bloque DATOS DEL EQUIPO. Por defecto 30 días."
          type="number"
          value={form.validity_days ?? 30}
          onChange={(v) => setF("validity_days", Number(v) || 0)}
          testid="design-validity-days"
        />
      )}
      {tplKey !== "quote" && (
        <Field label="Términos y condiciones para documentos PDF (pie de página)" hint="Texto libre que aparecerá en el pie de página de facturas y albaranes. Admite saltos de línea. Si está vacío, no se muestra." value={form.footer_text} onChange={(v) => setF("footer_text", v)} multi testid="design-footer" />
      )}

      <div>
        <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
          Mostrar importes
        </label>
        <label className="mt-1 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!form.show_amounts} onChange={(e) => setF("show_amounts", e.target.checked)} className="accent-[#6E8B3D]" data-testid="design-show-amounts" />
          Mostrar precios, IVA y totales en el PDF (desactívalo para albaranes sin importes)
        </label>
      </div>

      <div>
        <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
          Columnas visibles en la tabla de líneas
        </label>
        <div className="mt-1 grid grid-cols-2 md:grid-cols-3 gap-1.5">
          {Object.entries(COLUMN_LABELS).map(([k, lbl]) => (
            <label key={k} className={`flex items-center gap-2 border px-2 py-1.5 cursor-pointer text-xs ${
              (form.columns_visible || []).includes(k) ? "border-[#6E8B3D] bg-[#F0F4E8]" : "border-[#E5E5E0]"
            }`}>
              <input
                type="checkbox"
                checked={(form.columns_visible || []).includes(k)}
                onChange={() => toggleCol(k)}
                data-testid={`design-col-${k}`}
                className="accent-[#6E8B3D]"
              />
              {lbl}
            </label>
          ))}
        </div>
      </div>
        </>
      )}

      <div className="pt-2">
        <button
          onClick={() => onSave(form)}
          data-testid="design-save"
          className="inline-flex items-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-4 py-2 text-sm font-semibold"
        >
          <Save size={14} /> Guardar plantilla
        </button>
      </div>
    </div>
  );
}

function EmailEditor({ tplKey, tpl, onSave, onReset }) {
  const [form, setForm] = useState(tpl);
  const [preview, setPreview] = useState(null);
  const [showVars, setShowVars] = useState(false);
  const isWhatsappTemplate = tplKey === "notify_with_repair_whatsapp"
    || tplKey === "notify_without_repair_whatsapp"
    || tplKey === "notify_received_no_quote_whatsapp"
    || tplKey === "quote_send_whatsapp"
    || tplKey === "checklist_offer_whatsapp";
  // Todos los emails (no-WhatsApp) son HTML5 desde iteración 64.
  const isHtmlTemplate = !isWhatsappTemplate;
  useEffect(() => setForm(tpl), [tpl, tplKey]);

  const setF = (k, v) => setForm({ ...form, [k]: v });

  const doPreview = async () => {
    try {
      const r = await api.get(`/templates/emails/${tplKey}/preview`);
      setPreview(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="ds-card p-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#6E8B3D]">
            // {EMAILS.find((d) => d.key === tplKey)?.label}
          </div>
          <div className="font-display font-bold text-xl mt-1">
            {isWhatsappTemplate ? "Plantilla de WhatsApp" : "Plantilla del email"}
          </div>
          <p className="text-xs text-[#525252] mt-1">
            {isWhatsappTemplate
              ? "Texto plano que se prellena al abrir wa.me. Soporta variables como {{cliente_nombre}}, {{equipo}}, {{reparacion_id}}, {{importe_total}}."
              : isHtmlTemplate
                ? "Email HTML5 enviado al cliente. Soporta variables como {{cliente_nombre}}, {{reparacion_id}}, {{importe_total}}, {{firma_cliente}}, etc."
                : "Se usa al enviar este tipo de email desde la aplicación."}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={doPreview} data-testid="design-email-preview" className="border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-2 text-xs font-mono uppercase inline-flex items-center gap-1">
            <Eye size={12} /> Vista previa
          </button>
          <button onClick={onReset} data-testid="design-reset" className="border border-red-300 text-red-700 hover:bg-red-50 px-3 py-2 text-xs font-mono uppercase inline-flex items-center gap-1">
            <RotateCcw size={12} /> Restaurar
          </button>
        </div>
      </div>

      {!isWhatsappTemplate && (
        <Field label="Asunto" value={form.subject} onChange={(v) => setF("subject", v)} testid="design-subject" />
      )}
      {isHtmlTemplate ? (
        <>
          <Field
            label="Cuerpo HTML5 del email"
            value={form.body_html || ""}
            onChange={(v) => setF("body_html", v)}
            multi
            rows={20}
            hint="Pega o edita HTML directamente. Compatible con Gmail, Outlook, Apple Mail y móviles. Las variables {{...}} se sustituyen al enviar."
            testid="design-body-html"
          />
          <div className="border border-[#E5E5E0] bg-[#FAFAF7]">
            <button
              type="button"
              onClick={() => setShowVars((v) => !v)}
              data-testid="design-vars-toggle"
              className="w-full px-3 py-2 text-left font-mono text-[10px] uppercase tracking-widest text-[#525252] hover:bg-white inline-flex items-center justify-between"
            >
              <span>// Variables disponibles ({EMAIL_VARS.length})</span>
              <span>{showVars ? "−" : "+"}</span>
            </button>
            {showVars && (
              <div className="px-3 py-3 border-t border-[#E5E5E0] grid grid-cols-2 md:grid-cols-3 gap-1.5 text-[11px] font-mono">
                {EMAIL_VARS.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(`{{${v}}}`);
                      toast.success(`{{${v}}} copiado`);
                    }}
                    data-testid={`design-vars-chip-${v}`}
                    className="text-left px-2 py-1 border border-[#E5E5E0] bg-white hover:border-[#111110] hover:bg-[#111110] hover:text-white transition-colors"
                    title="Click para copiar"
                  >
                    {`{{${v}}}`}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <Field
          label={isWhatsappTemplate ? "Mensaje de WhatsApp" : "Cuerpo del email"}
          value={form.body}
          onChange={(v) => setF("body", v)}
          multi
          rows={isWhatsappTemplate ? 8 : 10}
          testid="design-body"
        />
      )}

      {tplKey === "customer_signature_request" && <SignatureTtlField />}

      <div className="pt-2">
        <button
          onClick={() => onSave(form)}
          data-testid="design-save"
          className="inline-flex items-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-4 py-2 text-sm font-semibold"
        >
          <Save size={14} /> Guardar plantilla
        </button>
      </div>

      {preview && (
        <div className="border-t border-[#E5E5E0] pt-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-2">
            // Vista previa (datos de ejemplo)
          </div>
          <div className="bg-[#F7F7F4] border border-[#E5E5E0] p-4 text-sm" data-testid="design-email-preview-content">
            <div className="font-bold mb-2">Asunto: {preview.subject}</div>
            {isHtmlTemplate && preview.body_html ? (
              <iframe
                title="Vista previa HTML"
                srcDoc={preview.body_html}
                className="w-full h-96 border border-[#E5E5E0] bg-white"
                data-testid="design-email-preview-iframe"
              />
            ) : (
              <div className="text-[#111110] whitespace-pre-wrap">{preview.body}</div>
            )}
          </div>
          <button onClick={() => setPreview(null)} className="mt-2 text-xs text-[#525252] hover:underline inline-flex items-center gap-1">
            <X size={11} /> Cerrar vista previa
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, multi, rows = 3, hint, testid, type }) {
  const Cmp = multi ? "textarea" : "input";
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">{label}</label>
      {hint && <span className="ml-2 text-[10px] text-[#525252] italic">· {hint}</span>}
      <Cmp
        type={!multi ? (type || "text") : undefined}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={multi ? rows : undefined}
        data-testid={testid}
        className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none font-mono"
      />
    </div>
  );
}

export function LogoUploader({ value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("El logo no puede superar 2 MB");
      e.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await fetch(`${api.defaults.baseURL}/settings/logo`, fetchAuthOpts({
        method: "POST",
        body: fd,
      }));
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || "Error subiendo logo");
      } else {
        onChange(data.logo_data_b64);
        toast.success("Logo actualizado. Aparecerá en la esquina superior derecha de los PDFs.");
      }
    } catch {
      toast.error("Error subiendo logo");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar el logo? Los PDFs se generarán sin logo.")) return;
    try {
      await api.delete("/settings/logo");
      onChange("");
      toast.success("Logo eliminado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="ds-card p-4 flex items-center gap-4 flex-wrap">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#6E8B3D] shrink-0">
        // Logo para documentos
      </div>
      <div className="w-32 h-16 border border-dashed border-[#E5E5E0] bg-[#F7F7F4] flex items-center justify-center overflow-hidden">
        {value ? (
          <img src={value} alt="Logo de la empresa" className="max-w-full max-h-full object-contain" data-testid="design-logo-preview" />
        ) : (
          <ImageIcon size={20} className="text-[#a0a0a0]" />
        )}
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="text-sm">
          {value ? "Logo configurado. Aparece en la esquina superior derecha de los PDFs." : "Sin logo. Sube una imagen para que aparezca en los PDFs."}
        </div>
        <div className="text-[10px] text-[#525252] mt-0.5">
          Formatos: JPG, JPEG, PNG, WEBP · Tamaño máx 2 MB · Se escala automáticamente a 40 mm de ancho.
        </div>
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid="design-logo-file" />
      <button
        onClick={onPick}
        disabled={uploading}
        data-testid="design-logo-upload"
        className="border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-2 text-xs font-mono uppercase inline-flex items-center gap-1 disabled:opacity-50"
      >
        <Upload size={12} /> {uploading ? "Subiendo..." : value ? "Cambiar logo" : "Subir logo"}
      </button>
      {value && (
        <button
          onClick={remove}
          data-testid="design-logo-remove"
          className="border border-red-300 text-red-700 hover:bg-red-50 px-3 py-2 text-xs font-mono uppercase inline-flex items-center gap-1"
        >
          <Trash2 size={12} /> Eliminar
        </button>
      )}
    </div>
  );
}



const LABEL_VARS_LEGACY_IGNORED = [];  // Iter 105 — sustituido por endpoint /api/templates/available-variables

function LabelLinesEditor({ form, setF }) {
  const lines = Array.isArray(form.lines) ? form.lines : [];
  const inputRefs = useRef({}); // idx → HTMLInputElement
  const [focusIdx, setFocusIdx] = useState(null);
  // Iter 105 — Variables enriquecidas desde el backend (registro central).
  const [variables, setVariables] = useState([]);
  useEffect(() => {
    api.get("/templates/available-variables")
      .then((r) => setVariables(r.data?.variables || []))
      .catch(() => setVariables([]));
  }, []);
  const varsByGroup = useMemo(() => {
    const map = new Map();
    for (const v of variables) {
      if (!map.has(v.group)) map.set(v.group, []);
      map.get(v.group).push(v);
    }
    return Array.from(map.entries());
  }, [variables]);

  const setLine = (i, v) => setF("lines", lines.map((x, idx) => (idx === i ? v : x)));
  const removeLine = (i) => setF("lines", lines.filter((_, idx) => idx !== i));
  const addLine = (v) => {
    setF("lines", [...lines, v]);
    // enfocar la nueva línea al final del texto
    setTimeout(() => {
      const idx = lines.length;
      const el = inputRefs.current[idx];
      if (el) {
        el.focus();
        el.selectionStart = el.selectionEnd = el.value.length;
        setFocusIdx(idx);
      }
    }, 30);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= lines.length) return;
    const copy = [...lines];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setF("lines", copy);
  };

  // Iter 105 — Inserta {variable} en la posición del cursor de la línea con
  // foco actual. Si no hay línea con foco, añade una nueva línea al final.
  const insertVarAtCursor = (varKey) => {
    const token = `{${varKey}}`;
    if (focusIdx == null || !inputRefs.current[focusIdx]) {
      addLine(token);
      return;
    }
    const el = inputRefs.current[focusIdx];
    const val = lines[focusIdx] || "";
    const start = el.selectionStart ?? val.length;
    const end = el.selectionEnd ?? val.length;
    const next = val.slice(0, start) + token + val.slice(end);
    setLine(focusIdx, next);
    // Restaurar cursor tras el token insertado
    setTimeout(() => {
      const same = inputRefs.current[focusIdx];
      if (same) {
        same.focus();
        const pos = start + token.length;
        same.selectionStart = same.selectionEnd = pos;
      }
    }, 30);
  };

  return (
    <div className="space-y-3" data-testid="label-lines-editor">
      <div className="border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 leading-relaxed">
        <strong>Iter 81:</strong> temporalmente la etiqueta se imprime en <strong>A4 vertical</strong>
        con la etiqueta centrada y tipografía grande. Cuando conectes una impresora térmica podremos
        activar el resto de formatos.
      </div>

      <div>
        <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
          Formato de impresión
        </label>
        <select
          value={form.print_format || "A4"}
          onChange={(e) => setF("print_format", e.target.value)}
          data-testid="design-label-format"
          className="mt-1 w-full max-w-sm ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
        >
          <option value="A4">A4 (predeterminado)</option>
          <option value="STICKER_100X150" disabled>100 × 150 mm (próximamente)</option>
          <option value="STICKER_62X29" disabled>62 × 29 mm (próximamente)</option>
          <option value="STICKER_50X30" disabled>50 × 30 mm (próximamente)</option>
          <option value="CUSTOM" disabled>Personalizado (próximamente)</option>
        </select>
        <div className="text-[11px] text-[#525252] italic mt-1">
          Sólo A4 está disponible actualmente. Los tamaños de etiqueta térmica se activarán en una futura versión.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field
          label="Cabecera (línea destacada, arriba)"
          hint="Por defecto: número de reparación. Soporta variables."
          value={form.header_text}
          onChange={(v) => setF("header_text", v)}
          testid="design-label-header"
        />
        <Field
          label="Tamaño de fuente (pt)"
          type="number"
          value={form.font_size ?? 9}
          onChange={(v) => setF("font_size", Number(v) || 9)}
          testid="design-label-font"
        />
      </div>

      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!form.show_repair_number_header}
            onChange={(e) => setF("show_repair_number_header", e.target.checked)}
            className="accent-[#6E8B3D]"
            data-testid="design-label-show-header"
          />
          Mostrar línea de cabecera
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!form.show_logo}
            onChange={(e) => setF("show_logo", e.target.checked)}
            className="accent-[#6E8B3D]"
            data-testid="design-label-show-logo"
          />
          Mostrar logo
        </label>
      </div>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-2">
          Líneas a imprimir (en orden)
        </div>
        <div className="space-y-2">
          {lines.length === 0 && (
            <div className="text-xs text-[#525252] italic">
              No hay líneas. Añade alguna desde &quot;Variables disponibles&quot;.
            </div>
          )}
          {lines.map((line, i) => (
            <div key={i} className="flex items-center gap-1.5" data-testid={`design-label-line-${i}`}>
              <input
                ref={(el) => { inputRefs.current[i] = el; }}
                value={line}
                onChange={(e) => setLine(i, e.target.value)}
                onFocus={() => setFocusIdx(i)}
                className={`flex-1 bg-white border ${focusIdx === i ? "border-[#6E8B3D] ring-1 ring-[#6E8B3D]" : "border-[#E5E5E0]"} focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none font-mono`}
              />
              <button
                onClick={() => move(i, -1)}
                disabled={i === 0}
                className="px-2 py-1 border border-[#E5E5E0] text-xs disabled:opacity-30"
                title="Subir"
              >↑</button>
              <button
                onClick={() => move(i, 1)}
                disabled={i === lines.length - 1}
                className="px-2 py-1 border border-[#E5E5E0] text-xs disabled:opacity-30"
                title="Bajar"
              >↓</button>
              <button
                onClick={() => removeLine(i)}
                className="px-2 py-1 border border-red-300 text-red-700 text-xs hover:bg-red-50"
                title="Quitar línea"
                data-testid={`design-label-remove-${i}`}
              ><Trash2 size={12} /></button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => addLine("")}
            data-testid="design-label-add-empty"
            className="w-full mt-1 border border-dashed border-[#B5B5B5] hover:border-[#6E8B3D] hover:bg-[#F0F4E8] px-3 py-1.5 text-xs text-[#525252]"
          >
            + Añadir línea vacía
          </button>
        </div>
      </div>

      {/* Iter 105 — Panel de Variables Disponibles enriquecido */}
      <div className="border-t border-[#E5E5E0] pt-3">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-1">
          Variables disponibles ({variables.length})
        </div>
        <div className="text-[11px] text-[#525252] italic mb-3">
          Haz clic para insertar en el cursor de la línea seleccionada (
          {focusIdx == null
            ? "ninguna línea seleccionada — se añadirá al final"
            : `línea ${focusIdx + 1}`}
          ). También puedes escribir manualmente <code className="bg-white border px-1">{"{variable}"}</code> en cualquier línea.
        </div>
        <div className="space-y-3" data-testid="label-variables-panel">
          {varsByGroup.map(([group, opts]) => (
            <div key={group}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#6E8B3D] mb-1">
                {group} ({opts.length})
              </div>
              <div className="flex flex-wrap gap-1.5">
                {opts.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => insertVarAtCursor(opt.key)}
                    className="px-2 py-1 border border-[#E5E5E0] hover:border-[#6E8B3D] hover:bg-[#F0F4E8] text-xs font-mono"
                    data-testid={`design-label-add-${opt.key}`}
                    title={`Insertar {${opt.key}} — ejemplo: ${opt.example || ""}`}
                  >
                    + {opt.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function SignatureTtlField() {
  // Iter 79 — Validez del enlace de firma (días). Vive en settings.billing
  // porque ya existía como `signature_link_ttl_days`. Lo exponemos junto a la
  // plantilla "customer_signature_request" para que el admin lo encuentre
  // donde lógicamente pertenece.
  const [days, setDays] = useState(7);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const v = Number(r.data?.billing?.signature_link_ttl_days) || 7;
      setDays(v);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    if (!Number.isFinite(days) || days < 1 || days > 365) {
      toast.error("Indica un número de días entre 1 y 365");
      return;
    }
    setBusy(true);
    try {
      await api.put("/settings", { billing: { signature_link_ttl_days: Math.round(days) } });
      toast.success(`Validez del enlace: ${Math.round(days)} día${days === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="border-2 border-[#6E8B3D] bg-[#F4F8EC] p-4" data-testid="signature-ttl-field">
      <label className="font-mono text-[10px] uppercase tracking-widest text-[#6E8B3D] font-bold">
        Validez del enlace de firma
      </label>
      <div className="text-xs text-[#525252] italic mt-0.5">
        Número de días durante los que el cliente podrá firmar desde el enlace recibido.
      </div>
      <div className="mt-2 flex items-center gap-2">
        <input
          type="number" min={1} max={365}
          value={loaded ? days : ""}
          onChange={(e) => setDays(Number(e.target.value) || 0)}
          data-testid="signature-ttl-input"
          className="w-24 ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm font-mono"
        />
        <span className="text-sm text-[#525252]">día{days === 1 ? "" : "s"}</span>
        <button
          type="button"
          onClick={save}
          disabled={busy || !loaded}
          data-testid="signature-ttl-save"
          className="ml-2 px-3 py-2 text-xs uppercase tracking-wider bg-[#111110] text-white hover:bg-[#2a2a28] font-semibold disabled:bg-[#A8A8A0]"
        >
          {busy ? "Guardando…" : "Guardar validez"}
        </button>
      </div>
      <div className="text-[11px] text-[#525252] mt-2">
        Por defecto: <b>7 días</b>. Las nuevas solicitudes usarán este valor; las solicitudes ya generadas mantienen su fecha original.
      </div>
    </div>
  );
}
